import React, {useRef, useState, useEffect} from 'react';
import styles from "./ADSPRoductSelectorMain.module.scss";
import IcArrow from "./ADSProductSelectorArrow.svg";
import {useADSContext} from "../../../../../../../../context/ADSProvider";
import ADSImage from "../../../../../../../ADSImage";
import ADSProductDelete from "../ADSProductDelete";
import ADSProductDownload from "../ADSProductDownload";
import ADSProductFavorite from "../ADSProductFavorite";
import ADSProductCheckbox from "../ADSProductCheckbox";
import {useImageEditorContext} from "../../../../../../../../context/ImageEditorProvider";

const ADSProductSelectorMain = () => {
  const {products, totalProducts, selectedProduct, handleSelectProduct, setProductFilter, loadingProducts} = useADSContext();
  const {setIsEditingImage} = useImageEditorContext();

  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onSelectProduct = (product) => {
    handleSelectProduct(product);
    setIsEditingImage(false);
  }

  const scrollToStart = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  };

  const scrollToEnd = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  };

  const handleShowMore = () => {
    setProductFilter(prev => ({
      ...prev,
      page: prev.page + 1
    }))
  }

  // Mouse down event handler
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;

    setIsDragging(true);
    // Store starting position
    setStartX(e.pageX);
    // Store current scroll position
    setScrollLeft(scrollRef.current.scrollLeft);
    // Change cursor
    document.body.style.cursor = 'grabbing';
  };

  // Mouse move event handler - immediate scrolling
  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;

    // Prevent default behaviors like text selection
    e.preventDefault();

    // Calculate distance moved from start position
    const x = e.pageX;
    const distance = x - startX;

    // Apply scroll directly - negative because we move content opposite to cursor
    scrollRef.current.scrollLeft = scrollLeft - distance;
  };

  // Mouse up event handler
  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = 'auto';
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e) => {
    if (!scrollRef.current) return;

    setIsDragging(true);
    setStartX(e.touches[0].pageX);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !scrollRef.current) return;

    const x = e.touches[0].pageX;
    const distance = x - startX;

    // Apply scroll - no multiplier to keep 1:1 tracking with finger
    scrollRef.current.scrollLeft = scrollLeft - distance;
  };

  // Clean up event listeners
  useEffect(() => {
    const handleMouseUpOutside = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.cursor = 'auto';
      }
    };

    // Add global event listeners to handle cases where mouse is released outside the container
    document.addEventListener('mouseup', handleMouseUpOutside);
    document.addEventListener('mouseleave', handleMouseUpOutside);

    return () => {
      document.removeEventListener('mouseup', handleMouseUpOutside);
      document.removeEventListener('mouseleave', handleMouseUpOutside);
      document.body.style.cursor = 'auto'; // Reset cursor on unmount
    };
  }, [isDragging]);

  const showMore = Number(totalProducts) > products?.length && !loadingProducts
  const showLoading = Number(totalProducts) > products?.length && loadingProducts

  return (
    <div className={styles.selector}>
      <img src={IcArrow} className={styles.arrow} alt='icArrow' onClick={scrollToStart}/>

      <div
        className={styles.mainSelectorWrapper}
        ref={scrollRef}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          // Turn off smooth scrolling during drag for immediate response
          scrollBehavior: isDragging ? 'auto' : 'smooth'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {products.map(product => {
          const isSelectedProduct = selectedProduct.id === product.id;
          const imagePath = product.imageEditedThumbnailUrl || product.imageThumbnailUrl
          return (
            <div key={product.id} className={styles.mainSelector} onClick={() => onSelectProduct(product)}>
              <ADSImage className={styles.image} path={imagePath} draggable={false}/>
              <div className={styles.mainSelectorActions}>
                <ADSProductDelete id={product.id}/>
                <ADSProductDownload product={product}/>
                <ADSProductFavorite id={product.id} favorite={product.favorite}/>
                <ADSProductCheckbox
                  id={product.id}
                  selected={isSelectedProduct}
                  onSelect={() => onSelectProduct(product)}/>
              </div>
            </div>
          )
        })}

        {showLoading && <div className={styles.moreCreations}>Loading..</div>}
        {showMore && <div className={styles.moreCreations} onClick={handleShowMore}>More..</div>}
      </div>

      <img src={IcArrow} className={`${styles.arrow} ${styles.rotate180}`} alt='icArrow' onClick={scrollToEnd}/>
    </div>
  );
};

export default ADSProductSelectorMain;
